import { Modal, Descriptions, Spin, message, Tag } from "antd";
import {
  UserOutlined,
  MailOutlined,
  EnvironmentOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useIntl } from "@umijs/max";
import { getOperator, type OperatorInfo } from "@/services/system";
import { useUserStore } from "@/stores/useUserStore";
import { useDictStore } from "@/stores/useDictStore";
import dayjs from "dayjs";

interface PersonalInfoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const agentLevelMap: Record<number, string> = {
  2: "menu.account.personal_info.agentLevel.2",
  3: "menu.account.personal_info.agentLevel.3",
  4: "menu.account.personal_info.agentLevel.4",
};

const PersonalInfoModal: React.FC<PersonalInfoModalProps> = ({
  open,
  onOpenChange,
}) => {
  const intl = useIntl();
  const { userInfo } = useUserStore();
  const { dictMap, fetchDict } = useDictStore();
  const [loading, setLoading] = useState(false);
  const [operatorInfo, setOperatorInfo] = useState<OperatorInfo | null>(null);

  const regionMap = useMemo(() => {
    const list = dictMap["region"] || [];
    return new Map(list.map((item: any) => [item.dictValue, item.dictLabel]));
  }, [dictMap]);

  useEffect(() => {
    fetchDict("region");
  }, [fetchDict]);

  const fetchOperatorInfo = useCallback(async () => {
    if (userInfo?.operatorId === undefined || userInfo?.operatorId === null) return;
    setLoading(true);
    try {
      const res = await getOperator({ id: userInfo.operatorId });
      setOperatorInfo(res);
    } catch (error: any) {
      if (!error?.isGlobalHandled) {
        message.error(
          intl.formatMessage({
            id: "menu.account.personal_info.fetchFailed",
          }),
        );
      }
    } finally {
      setLoading(false);
    }
  }, [userInfo?.operatorId, intl]);

  useEffect(() => {
    if (open) {
      fetchOperatorInfo();
    } else {
      setOperatorInfo(null);
    }
  }, [open, fetchOperatorInfo]);

  const formatTime = (timestamp: number) => {
    if (!timestamp) return "-";
    return dayjs(timestamp * 1000).format("YYYY-MM-DD HH:mm:ss");
  };

  const walletModeLabel =
    operatorInfo?.walletMode === "1"
      ? intl.formatMessage({
          id: "menu.account.personal_info.walletMode.transfer",
        })
      : intl.formatMessage({
          id: "menu.account.personal_info.walletMode.single",
        });

  const statusLabel =
    operatorInfo?.status === 0 ? (
      <Tag color="green">
        {intl.formatMessage({ id: "menu.account.personal_info.status.normal" })}
      </Tag>
    ) : (
      <Tag color="red">
        {intl.formatMessage({ id: "menu.account.personal_info.status.frozen" })}
      </Tag>
    );

  return (
    <Modal
      title={intl.formatMessage({ id: "menu.account.personal_info" })}
      open={open}
      onCancel={() => onOpenChange(false)}
      footer={null}
      width={840}
      destroyOnHidden
      styles={{
        body: { maxHeight: "calc(100vh - 200px)", overflow: "auto" },
      }}
    >
      {loading || !operatorInfo ? (
        <div style={{ textAlign: "center", padding: "40px 0" }}>
          <Spin size="large" />
        </div>
      ) : (
        <Descriptions
          column={2}
          bordered
          size="small"
          style={{ marginTop: 16 }}
          labelStyle={{ whiteSpace: "nowrap" }}
        >
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.id",
            })}
          >
            {operatorInfo.id ?? "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.appid",
            })}
          >
            {operatorInfo.appid || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.operatorName",
            })}
          >
            <UserOutlined style={{ marginRight: 8 }} />
            {operatorInfo.operatorName || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.operatorManagerName",
            })}
          >
            {operatorInfo.operatorManagerName || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.contactInformation",
            })}
          >
            <MailOutlined style={{ marginRight: 8 }} />
            {operatorInfo.contactInformation || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.userName",
            })}
          >
            {operatorInfo.userName || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.pid",
            })}
          >
            {operatorInfo.pid === 0
              ? intl.formatMessage({
                  id: "menu.account.personal_info.pid.none",
                })
              : operatorInfo.pid || "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.agentLevel",
            })}
          >
            {operatorInfo.agentLevel
              ? intl.formatMessage({
                  id:
                    agentLevelMap[operatorInfo.agentLevel] ||
                    "menu.account.personal_info.agentLevel.unknown",
                })
              : "-"}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.walletMode",
            })}
          >
            <WalletOutlined style={{ marginRight: 8 }} />
            {walletModeLabel}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.region",
            })}
          >
            <EnvironmentOutlined style={{ marginRight: 8 }} />
            {operatorInfo.region
              ? regionMap.get(operatorInfo.region) || operatorInfo.region
              : "-"}
          </Descriptions.Item>
          {operatorInfo.agentLevel === 2 && (
            <Descriptions.Item
              label={intl.formatMessage({
                id: "menu.account.personal_info.fee",
              })}
            >
              {operatorInfo.fee !== undefined ? `${operatorInfo.fee}%` : "-"}
            </Descriptions.Item>
          )}
          {operatorInfo.agentLevel === 4 && (
            <Descriptions.Item
              label={intl.formatMessage({
                id: "menu.account.personal_info.ipList",
              })}
              span={2}
            >
              {operatorInfo.ipList || "-"}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.status",
            })}
          >
            {statusLabel}
          </Descriptions.Item>
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.remark",
            })}
          >
            {operatorInfo.remark || "-"}
          </Descriptions.Item>
          {operatorInfo.agentLevel === 4 && (
            <Descriptions.Item
              label={intl.formatMessage({
                id: "menu.account.personal_info.publicKey",
              })}
              span={2}
            >
              {operatorInfo.publicKey || "-"}
            </Descriptions.Item>
          )}
          <Descriptions.Item
            label={intl.formatMessage({
              id: "menu.account.personal_info.createdAt",
            })}
          >
            {formatTime(operatorInfo.createdAt)}
          </Descriptions.Item>
        </Descriptions>
      )}
    </Modal>
  );
};

export default PersonalInfoModal;
